import { useState, useCallback, type ReactNode, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const DURATION_OPTIONS = [30, 45, 60, 90, 120] as const;

interface ServicesSetupStepProps {
  readonly onComplete: (data: { name: string; duration: number; price: number }) => void;
}

export function ServicesSetupStep({ onComplete }: ServicesSetupStepProps): ReactNode {
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const newErrors: Record<string, string> = {};
      if (!name.trim()) newErrors["name"] = "\u05E9\u05DD \u05D4\u05E9\u05D9\u05E8\u05D5\u05EA \u05D7\u05D5\u05D1\u05D4";
      const priceNum = Number(price);
      if (!price || priceNum <= 0) newErrors["price"] = "\u05D4\u05D6\u05D9\u05E0\u05D5 \u05DE\u05D7\u05D9\u05E8 \u05EA\u05E7\u05D9\u05DF";
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setErrors({});
      onComplete({
        name: name.trim(),
        duration,
        price: Math.round(priceNum * 100),
      });
    },
    [name, duration, price, onComplete],
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 px-4 py-6">
      <h2 className="text-xl font-bold">{"\u05D4\u05E9\u05D9\u05E8\u05D5\u05EA \u05D4\u05E8\u05D0\u05E9\u05D5\u05DF \u05E9\u05DC\u05DB\u05DD"}</h2>
      <p className="text-sm text-muted-foreground">
        {"\u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05E9\u05D9\u05E8\u05D5\u05EA \u05D0\u05D7\u05D3 \u05DC\u05D4\u05EA\u05D7\u05DC\u05D4. \u05EA\u05D5\u05DB\u05DC\u05D5 \u05DC\u05D4\u05D5\u05E1\u05D9\u05E3 \u05E2\u05D5\u05D3 \u05DE\u05D4\u05E4\u05D0\u05E0\u05DC \u05D1\u05D4\u05DE\u05E9\u05DA."}
      </p>

      <Card>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="serviceName" className="text-sm font-medium">
              {"\u05E9\u05DD \u05D4\u05E9\u05D9\u05E8\u05D5\u05EA"}
            </label>
            <Input
              id="serviceName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder={"\u05DC\u05DE\u05E9\u05DC: \u05EA\u05E1\u05E4\u05D5\u05E8\u05EA \u05D2\u05D1\u05E8\u05D9\u05DD"}
            />
            {errors["name"] !== undefined && (
              <p className="text-sm text-destructive">{errors["name"]}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">{"\u05DE\u05E9\u05DA \u05D4\u05E9\u05D9\u05E8\u05D5\u05EA"}</label>
            <div className="flex flex-wrap gap-2">
              {DURATION_OPTIONS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  className={`min-h-[44px] rounded-md border px-4 py-2 text-sm transition-colors ${
                    duration === d
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input hover:bg-accent"
                  }`}
                >
                  {d < 60 ? `${d} \u05D3\u05E7\u05F3` : `${d / 60} \u05E9\u05E2\u05D5\u05EA`}
                  {d === 90 ? " (1.5)" : ""}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="servicePrice" className="text-sm font-medium">
              {"\u05DE\u05D7\u05D9\u05E8 (\u20AA)"}
            </label>
            <Input
              id="servicePrice"
              type="number"
              dir="ltr"
              min="0"
              step="1"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="min-h-[44px] text-base"
              placeholder="0"
            />
            {errors["price"] !== undefined && (
              <p className="text-sm text-destructive">{errors["price"]}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Button type="submit" size="lg" className="min-h-[44px]">
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </form>
  );
}
